import { initAnimations } from './animations.js';
import { ILLUS } from './illustrations.js';
import { $, $$, el, wrapEach, activate } from './dom.js';
import { METIERS } from './metiers.js';
import { renderEndServices, hideMarkOverFooter } from './footer.js';

const CONTACT_EMAIL = 'lusson.theophile@gmail.com';
const SCEAU = 'assets/img/logo-orange.png';
const LAZY = 'loading="lazy" decoding="async"';

const ARROW = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/></svg>';

const EQUIPE = [
  { nom: 'Théophile', art: 'social', photo: 'assets/img/theophile.jpg',
    desc: 'Votre premier interlocuteur : il écoute votre projet, vous propose la bonne approche, puis fait vivre votre marque au quotidien sur les réseaux.',
    points: ['Écoute & accompagnement commercial', 'Ligne éditoriale & publications', 'Animation des communautés'],
    quote: '« Créer de belles relations, faire grandir de vraies communautés. »' },
  { nom: 'Valentin', art: 'web', photo: 'assets/img/valentin.jpg',
    desc: 'Il conçoit et développe vos sites sur mesure, et configure des assistants IA qui prennent en charge ce qui vous fait perdre du temps.',
    points: ['Sites vitrines & e-commerce', 'Assistants IA sur mesure', 'Performance & référencement'],
    quote: '« Imaginer avec vous des outils simples, utiles et pensés pour vous faciliter la vie. »' },
  { nom: 'Marie', art: 'video', photo: 'assets/img/marie.jpg',
    desc: 'Elle filme votre activité, vos équipes et vos produits pour en tirer des images vivantes, prêtes à être partagées.',
    points: ['Films de marque', 'Reportages & coulisses', 'Formats verticaux'],
    quote: '« Mettre en lumière vos plus beaux moments et raconter ce qui vous ressemble. »' },
  { nom: 'Pierre', art: 'graph', photo: 'assets/img/pierre.jpg',
    desc: 'Il assemble les images, trouve le rythme et soigne chaque détail pour que vos vidéos se regardent jusqu’au bout.',
    points: ['Montage & rythme', 'Étalonnage & son', 'Déclinaisons par réseau'],
    quote: '« Donner vie à vos images pour faire ressentir toute l’émotion de votre histoire. »' },
  { nom: 'Nicolas', art: 'photo', photo: 'assets/img/nicolas.jpg',
    desc: 'Il photographie vos produits, vos lieux et les personnes qui font votre entreprise, avec une lumière qui vous ressemble.',
    points: ['Portraits & équipes', 'Produits & packshots', 'Reportages d’entreprise'],
    quote: '« Capturer avec douceur ces petits instants qui deviennent de grands souvenirs. »' }
];

const PAROLES = [
  { txt: 'Une équipe qui comprend vite et livre encore plus vite. En six mois, notre visibilité a doublé — et surtout, ce sont les bonnes personnes qui nous trouvent : celles qui savent déjà ce que l’on fait, et pourquoi.',
    nom: 'Camille Martin', role: 'Fondatrice', boite: 'Maison artisanale',
    photo: 'assets/img/valentin.jpg', logo: SCEAU },
  { txt: 'Du brief à la mise en ligne, tout est fluide : un seul interlocuteur, des points courts, des livrables au bon moment. On a enfin une image à la hauteur de notre produit, et des commerciaux qui la montrent.',
    nom: 'Thomas Bernard', role: 'Directeur marketing', boite: 'SaaS B2B',
    photo: 'assets/img/theophile.jpg', logo: SCEAU },
  { txt: 'Les contenus sont beaux, réguliers et surtout utiles. Nos réseaux ne sont plus une vitrine que l’on alimente par obligation : c’est devenu un vrai canal d’acquisition, avec des chiffres suivis chaque mois.',
    nom: 'Inès Laurent', role: 'Responsable communication', boite: 'Retail',
    photo: 'assets/img/valentin.jpg', logo: SCEAU },
  { txt: 'Wake Up a transformé une idée floue en campagne claire, rapide à produire et facile à suivre. Trois semaines ont suffi pour passer du fichier de notes au plan de diffusion, sans perdre le ton de la marque.',
    nom: 'Nicolas Moreau', role: 'Co-fondateur', boite: 'Start-up food',
    photo: 'assets/img/theophile.jpg', logo: SCEAU },
  { txt: 'Le site est plus lisible, plus rapide, et il travaille pour nous : depuis la refonte, les demandes entrantes sont mieux qualifiées et nos consultants passent moins de temps à expliquer notre métier.',
    nom: 'Sarah Petit', role: 'CEO', boite: 'Cabinet conseil',
    photo: 'assets/img/valentin.jpg', logo: SCEAU },
  { txt: 'Ils gèrent la stratégie, le tournage et la diffusion sans que l’on ait à multiplier les prestataires. Nos franchisés reçoivent des visuels prêts à publier, et le réseau parle enfin d’une seule voix.',
    nom: 'Julien Faure', role: 'Gérant', boite: 'Réseau de franchises',
    photo: 'assets/img/theophile.jpg', logo: SCEAU }
];

function siPasDImage(box) {
  const img = box && $('img', box);
  if (!img) return;
  const vide = () => { box.classList.add('is-empty'); img.remove(); };
  if (img.complete && !img.naturalWidth) vide();
  else img.addEventListener('error', vide);
}

function render(selector, items, build) {
  const deck = $(selector);
  if (!deck || deck.closest('[hidden]')) return;
  const frag = document.createDocumentFragment();
  items.forEach((item, i) => frag.appendChild(build(item, i)));
  deck.appendChild(frag);
}

function renderAll() {
  render('#indexDeck', METIERS, (m, i) => el(`
    <article class="serv${i === 0 ? ' is-on' : ''}" aria-label="${m.nom}">
      <div class="serv__art">${ILLUS[m.art] || ''}</div>
      <div class="serv__say">
        <h2 class="serv__name">${m.nom}</h2>
        <p class="serv__desc">${m.desc}</p>
        <button type="button" class="serv__go" data-besoin="${m.nom}"><span>Parler de ce projet</span>${ARROW}</button>
      </div>
    </article>`));

  render('#indexMenu', METIERS, (m, i) => el(
    `<li><button type="button" class="index__link${i === 0 ? ' is-on' : ''}" aria-current="${i === 0}"><span>${m.nom}</span></button></li>`
  ));

  render('#stackDeck', EQUIPE, (m) => {
    const slab = el(`
      <article class="slab">
        <div class="slab__grid">
          <div class="slab__face">
            <img src="${m.photo}" alt="${m.nom}" ${LAZY}>
            <i aria-hidden="true">${m.nom[0]}</i>
            <div class="slab__id"><h2 class="slab__name">${m.nom}</h2></div>
          </div>
          <div class="slab__side">
            <div class="slab__art">${ILLUS[m.art] || ''}</div>
            <div class="slab__txt">
              <p>${m.desc}</p>
              <ul class="slab__list">${wrapEach(m.points, 'li')}</ul>
              <p class="slab__quote">${m.quote}</p>
            </div>
          </div>
        </div>
      </article>`);
    siPasDImage($('.slab__face', slab));
    return slab;
  });

  render('#saysDeck', PAROLES, (p) => {
    const said = el(`
      <figure class="said">
        <blockquote class="said__txt">${p.txt}</blockquote>
        <span class="said__frame" aria-hidden="true">
          <img src="${p.photo}" alt="" ${LAZY}>
          <img class="said__seal" src="${SCEAU}" alt="" ${LAZY}>
        </span>
        <figcaption class="said__by">
          <span class="said__logo" aria-hidden="true"><img src="${p.logo}" alt="" ${LAZY}></span>
          <span class="said__id">
            <span class="said__who">${p.nom}</span>
            <span class="said__org">${p.role} · ${p.boite}</span>
          </span>
        </figcaption>
      </figure>`);
    siPasDImage($('.said__logo', said));
    siPasDImage($('.said__frame', said));
    const sceau = $('.said__seal', said);
    sceau.addEventListener('error', () => sceau.remove());
    return said;
  });

  render('#picks', METIERS, ({ nom }) => el(
    `<button type="button" class="pick" role="radio" aria-checked="false" data-besoin="${nom}"><span>${nom}</span></button>`
  ));
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const VALIDE = {
  nom: (v) => !!v,
  email: (v) => EMAIL_RE.test(v),
  message: (v) => v.length > 1
};

function short(text, max = 90) {
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const space = cut.lastIndexOf(' ');
  return (space > 50 ? cut.slice(0, space) : cut).trimEnd() + '…';
}

function initTalk(anim) {
  const form = $('#talkForm');
  if (!form) return null;

  const steps = $$('.step', form);
  const picks = $$('.pick', form);
  const rows = $$('.recap__row').map((row) => ({ row, kind: row.dataset.recap, val: $('.recap__val', row) }));
  const back = $('#talkBack');
  const nextTxt = $('.push__txt', $('#talkNext'));
  const arc = $('#talkArc');
  const status = $('#talkStatus');
  const hidden = $('#f-besoin');
  const last = steps.length - 1;

  // Sur mobile (ou mouvement réduit), le formulaire s'affiche en entier, sans étapes.
  const narrow = window.matchMedia('(max-width: 900px)');
  const isFlat = () => anim.reduced || narrow.matches;

  let at = 0;
  let sent = false;

  const valueOf = (name) => {
    const node = document.getElementById('f-' + name);
    return node ? node.value.trim() : '';
  };

  function choose(pick) {
    hidden.value = pick.dataset.besoin;
    activate(picks, (p) => p === pick, 'aria-checked');
    writeRecap();
  }

  function pickBesoin(name) {
    const pick = picks.find((p) => p.dataset.besoin === name);
    if (pick) choose(pick);
  }

  function check(index) {
    const step = steps[index];
    const rule = VALIDE[step.dataset.step];
    const ok = !rule || rule(valueOf(step.dataset.step));
    step.classList.toggle('is-wrong', !ok);
    return ok;
  }

  function writeRecap() {
    const here = isFlat() ? '' : steps[at].dataset.step;
    rows.forEach(({ row, kind, val }) => {
      const value = kind === 'besoin' ? hidden.value : valueOf(kind);
      if (val) val.textContent = value ? short(value) : 'À compléter';
      row.classList.toggle('is-set', !!value);
      row.classList.toggle('is-now', kind === here);
    });
  }

  function syncChrome() {
    const flat = isFlat();
    form.classList.toggle('is-flat', flat);
    back.hidden = flat;
    if (flat) {
      nextTxt.textContent = sent ? 'Envoyé' : 'Envoyer';
    } else {
      back.disabled = at === 0;
      nextTxt.textContent = sent ? 'Envoyé' : at === last ? 'Envoyer' : 'Continuer';
      if (arc) anim.arc(arc, (at + 1) / steps.length);
    }
    writeRecap();
  }

  function goto(index, dir) {
    if (index < 0 || index > last || index === at) return;
    const from = steps[at];
    const to = steps[index];
    at = index;
    anim.swapStep(from, to, dir);
    syncChrome();
    const field = $('input, textarea, .pick', to);
    if (field) setTimeout(() => field.focus({ preventScroll: true }), 420);
  }

  function forward() {
    if (isFlat()) {
      const wrong = steps.map((_, i) => check(i)).indexOf(false);
      if (wrong < 0) send();
      else $('input, textarea, .pick', steps[wrong])?.focus();
      return;
    }
    if (!check(at)) anim.shake(steps[at]);
    else if (at < last) goto(at + 1, 1);
    else send();
  }

  function send() {
    if (sent) return;
    const nom = valueOf('nom');
    const besoin = hidden.value;
    const subject = `Projet — ${besoin || 'Prise de contact'} — ${nom}`;
    const body = `Nom : ${nom}\nE-mail : ${valueOf('email')}\nBesoin : ${besoin}\n\n${valueOf('message')}`;
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

    sent = true;
    status.textContent = 'Votre messagerie s’ouvre avec le message prêt à partir — il ne reste qu’à l’envoyer.';
    status.classList.add('is-ok');
    anim.reveal(status);
    anim.celebrate();
    nextTxt.textContent = 'Envoyé';
  }

  picks.forEach((pick) => pick.addEventListener('click', () => choose(pick)));
  form.addEventListener('submit', (e) => { e.preventDefault(); forward(); });
  back.addEventListener('click', () => goto(at - 1, -1));
  form.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey && e.target.tagName === 'TEXTAREA') {
      e.preventDefault();
      forward();
    }
  });
  form.addEventListener('input', (e) => {
    const step = e.target.closest('.step');
    if (step && step.classList.contains('is-wrong')) check(steps.indexOf(step));
    writeRecap();
    status.style.opacity = '0';
  });

  narrow.addEventListener('change', () => {
    // Efface les styles laissés par les transitions d'étapes avant de changer de mise en page.
    steps.forEach((step) => step.removeAttribute('style'));
    syncChrome();
  });

  syncChrome();
  return { pickBesoin };
}

renderAll();
renderEndServices();
hideMarkOverFooter();

const anim = initAnimations();
const talk = initTalk(anim);
const contact = document.getElementById('contact');

$('#indexDeck')?.addEventListener('click', (e) => {
  const go = e.target.closest('.serv__go[data-besoin]');
  if (!go || !talk) return;
  talk.pickBesoin(go.dataset.besoin);
  anim.jumpTo(contact);
});
